import React, { useState, useMemo, useCallback, useEffect, useRef } from "react";
import { useLocation, useParams } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { secureGet } from "../../config/api";
import { useTranslation } from "react-i18next";
import PageScroller from "../../components/PageScroller";

import SnackbarNotification from "../../components/SnackbarNotification";
import Breadcrumbs from "../../components/Breadcrumbs";
import LoadingLogo from "../../components/LoadingLogo";
import Rows from "../../components/Rows";
import Filter from "../../components/Filter";

import checkIcon from "../../icons/checkfullcircle.svg";

const ZoneDetails = () => {
  const { t } = useTranslation();
  const { id: routeId } = useParams();
  const topRef = useRef(null);
  const location = useLocation();
  const zoneName = location?.state?.zoneName || "";
  const zoneId = String(routeId);

  const [loading, setLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });

  const [zoneDetails, setZoneDetails] = useState([]);
  const [locations, setLocations] = useState([]);

  const [filters, setFilters] = useState({ needle: "", location: "" });
  const [pageNumber, setPageNumber] = useState(0);
  const [pageSize, setPageSize] = useState(20);
  const [totalPages, setTotalPages] = useState(0);

  const abortController = useMemo(() => new AbortController(), []);
  const { signal } = abortController;

  const needleTimer = useRef(null);

  const showSnackbar = (message, severity = "success") =>
    setSnackbar({ open: true, message, severity });

  const buildQuery = (obj) => {
    const p = new URLSearchParams();
    Object.entries(obj).forEach(([k, v]) => {
      if (v !== undefined && v !== null && String(v).trim() !== "") p.set(k, String(v).trim());
    });
    return p.toString();
  };

  const pageRows = (data) => (Array.isArray(data) ? data : data?.content ?? []);

  const fetchLocations = useCallback(async () => {
    try {
      const qs = buildQuery({ zoneId, page: 0, size: 1000 });
      const res = await secureGet(`/inventory-locations?${qs}`, showSnackbar, { signal });
      setLocations(pageRows(res?.data));
    } catch (e) {
      if (e.name !== "AbortError") console.error("Error fetching locations:", e);
    }
  }, [zoneId, signal]);

  const fetchZoneDetails = useCallback(
    async (page = pageNumber, size = pageSize, active = filters) => {
      setLoading(true);
      try {
        const qs = buildQuery({
          page: String(page),
          size: String(size),
          zoneId: zoneId,
          needle: active.needle || "",
          locationId: active.location || "",
        });

        const url = qs
          ? `/inventory-zones/details?${qs}`
          : `/inventory-zones/details`;

        const response = await secureGet(url, showSnackbar, { signal });
        const data = response?.data ?? {};
        const fetched = data.content || [];

        setTotalPages(data.totalPages || 0);
        if (Number.isFinite(data.number)) setPageNumber(data.number);
        if (Number.isFinite(data.size)) setPageSize(data.size);

        setZoneDetails(fetched);
      } catch (error) {
        if (error.name !== "AbortError"){
          console.error("Failed to fetch zone details:", error);
          setZoneDetails([]);
          setTotalPages(0);
        }
      } finally {
        setLoading(false);
      }
    },
    [zoneId, signal, filters, pageNumber, pageSize]
  );

  useEffect(() => {
    (async () => {
      await fetchLocations();
      await fetchZoneDetails(0, pageSize, { needle: "", location: "" });
    })();
    return () => abortController.abort();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [zoneId]);

  const handleFilterChange = (name, value) => {
    const updated = { ...filters, [name]: value };
    setFilters(updated);

    if (name === "needle") {
      if (needleTimer.current) clearTimeout(needleTimer.current);
      needleTimer.current = setTimeout(() => {
        setPageNumber(0);
        fetchZoneDetails(0, pageSize, updated);
      }, 300);
    } else {
      setPageNumber(0);
      fetchZoneDetails(0, pageSize, updated);
    }
  };

  const resetFilters = () => {
    const reset = { needle: "", location: "" };
    setFilters(reset);
    setPageNumber(0);
    fetchZoneDetails(0, pageSize, reset);
  };

  const handlePageChange = (newPage) => {
    setPageNumber(newPage);
    fetchZoneDetails(newPage, pageSize, filters);
    topRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  const handlePageSizeChange = (newSize) => {
    const sizeNum = Number(newSize) || pageSize;
    setPageSize(sizeNum);
    setPageNumber(0);
    fetchZoneDetails(0, sizeNum, filters);
  };

  const columns = [
    { key: "reference", label: "Référence", width: '20%' },
    { key: "name", label: "Désignation", width: "30%" },
    { key: "locationName", label: "Emplacements", width: "20%" },
    { key: "countedQty", label: "Quantité comptée", width: "15%" },
    { key: "controlledQty", label: "Quantité contrôlée", width: "15%" },
  ];

  const coloredData = zoneDetails.map((row) => {
    const counted = row.countedQty;
    const controlled = row.controlledQty;
    const match = counted === controlled;

    return {
      ...row,
      reference: match ? (
        <span style={{ color: "#06D6A0", display: "flex", alignItems: "center", gap: "8px" }}>
          <img src={checkIcon} alt="✓" style={{ width: "16px", height: "16px" }} />
          {row.reference}
        </span>
      ) : row.reference,
      countedQty: <span style={{ color: "#FEA26F" }}>{counted}</span>,
      controlledQty: <span style={{ color: "#0283F4" }}>{controlled}</span>,
    };
  });

  const filterOptions = [
    { name: "needle", type: "search" },
    {
      name: "location",
      type: "dropdown",
      label: t("fields.inventory.location") || "Emplacement",
      options: locations.map((loc) => ({
        value: String(loc.id),
        label: loc.name || loc.barcode || `#${loc.id}`,
      })),
    },
  ];

  return (
    <div ref={topRef} style={{ position: "relative" }}>
      <Helmet>
        <title>GrayFox - {t("titles.zone_details")}</title>
      </Helmet>

      <div>
        <Breadcrumbs />
        <h1 className="text-[40px] font-semibold text-black">
          {t("titles.zone_details")}
          {zoneName ? (
            <span className="ml-3" style={{ color: "#6A78F8" }}>
              {zoneName}
            </span>
          ) : null}
        </h1>
      </div>

      <Filter
        filters={filterOptions}
        onFilterChange={handleFilterChange}
        resetFilters={resetFilters}
        filterValues={filters}
      />

      <div style={{ position: "relative", minHeight: 120 }}>
        {loading && (
          <div
            style={{
              position: "absolute",
              inset: 0,
              backgroundColor: "rgba(255,255,255,0.6)",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              zIndex: 1,
            }}
          >
            <LoadingLogo />
          </div>
        )}
        <Rows data={coloredData} columns={columns} />
      </div>
      {/* Pagination */}
      <div className="mt-6">
        <PageScroller
          pageNumber={pageNumber}
          totalPages={totalPages}
          onPageChange={handlePageChange}
          pageSize={pageSize}
          onPageSizeChange={handlePageSizeChange}
        />
      </div>
      <SnackbarNotification
        open={snackbar.open}
        message={snackbar.message}
        severity={snackbar.severity}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      />
    </div>
  );
};

export default ZoneDetails;
